import { useRef, useState } from "react";
import { AnimatePresence, motion, useScroll, useMotionValueEvent } from "framer-motion";
import { ArrowRight, Check, Plus } from "lucide-react";
import { valueLedgerV13 as copy } from "../config/media";

const EASE = [0.16, 1, 0.3, 1];
const N = copy.stages.length;

const BigNumber = ({ value, testid, tone = "light" }) => (
  <div
    data-testid={testid}
    className={`relative h-[150px] overflow-hidden lg:h-[190px] ${
      tone === "ink" ? "text-[var(--strollee-ink)]" : "text-[var(--strollee-bg)]"
    }`}
  >
    <AnimatePresence initial={false} mode="popLayout">
      <motion.span
        key={value}
        initial={{ y: "70%", opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: "-60%", opacity: 0 }}
        transition={{ duration: 0.5, ease: EASE }}
        className="block text-[150px] font-semibold leading-none tracking-[-0.06em] lg:text-[190px]"
      >
        {value}
      </motion.span>
    </AnimatePresence>
  </div>
);

// V1.3 Refinement 11 — "The ledger" (V1.3 ONLY).
// Split comparison: usual way accumulates 1→4 on scroll; Strollee stays 1 with the video full-height.
export const ValueSectionV13 = () => {
  const moduleRef = useRef(null);
  const [count, setCount] = useState(0);
  const complete = count >= N;

  const { scrollYProgress } = useScroll({
    target: moduleRef,
    offset: ["start 0.9", "start 0.2"],
  });

  useMotionValueEvent(scrollYProgress, "change", (v) => {
    const next = Math.min(N, Math.max(0, Math.floor(v * (N + 0.5))));
    setCount((prev) => (prev === next ? prev : next));
  });

  return (
    <section
      id="value"
      data-testid="value-section"
      className="bg-[var(--strollee-dark)] px-6 py-20 text-[var(--strollee-bg)] lg:px-10 lg:py-24"
    >
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-120px" }}
        transition={{ duration: 0.8, ease: EASE }}
        className="flex items-end justify-between gap-12"
      >
        <div className="max-w-[900px]">
          <p
            data-testid="value-eyebrow"
            className="text-xs font-semibold uppercase tracking-[0.22em] text-[#8FB89E]"
          >
            {copy.eyebrow}
          </p>
          <h2 className="mt-4 text-[40px] font-semibold leading-[1.06] tracking-[-0.03em] lg:text-[52px]">
            {copy.headlineLines.map((line) => (
              <span key={line} className="block">
                {line}
              </span>
            ))}
          </h2>
        </div>
        <p className="max-w-[340px] pb-2 text-base leading-relaxed text-[var(--strollee-bg)]/60">
          {copy.support}
        </p>
      </motion.div>

      {/* LEDGER MODULE */}
      <div
        ref={moduleRef}
        data-testid="value-ledger"
        className="mt-12 grid min-h-[78vh] grid-cols-2 overflow-hidden rounded-[var(--strollee-radius-hero)] border border-white/10 lg:mt-14"
      >
        {/* LEFT — the usual way */}
        <div className="flex flex-col bg-white/[0.04] p-10 lg:p-14">
          <p
            data-testid="value-usual-label"
            className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[var(--strollee-bg)]/50"
          >
            {copy.usualLabel}
          </p>
          <div className="mt-6 flex items-end gap-5">
            <BigNumber value={count} testid="value-usual-count" />
            <span className="pb-4 text-[15px] font-medium leading-snug text-[var(--strollee-bg)]/55 lg:pb-6">
              strollers
              <br />
              and counting
            </span>
          </div>

          <ul className="mt-8 flex flex-col" data-testid="value-stage-rows">
            {copy.stages.map((s, i) => {
              const on = i < count;
              return (
                <motion.li
                  key={s.id}
                  data-testid={`value-stage-${s.id}`}
                  data-active={on}
                  initial={false}
                  animate={{ opacity: on ? 1 : 0.28, x: on ? 0 : -6 }}
                  transition={{ duration: 0.45, ease: EASE }}
                  className="grid grid-cols-[120px_1fr_auto] items-center gap-6 border-t border-white/10 py-4 lg:grid-cols-[150px_1fr_auto]"
                >
                  <span className="text-[12px] font-semibold uppercase tracking-[0.14em] text-[var(--strollee-bg)]/50">
                    {s.stage}
                  </span>
                  <span className="text-[18px] font-semibold tracking-[-0.01em] lg:text-[20px]">
                    {s.buy}
                  </span>
                  <span
                    className={`flex h-8 w-8 items-center justify-center rounded-full border text-[12px] font-semibold transition-colors duration-300 ${
                      on ? "border-white/40 bg-white/10" : "border-white/15"
                    }`}
                  >
                    <Plus size={13} strokeWidth={2.4} />
                  </span>
                </motion.li>
              );
            })}
          </ul>

          <motion.div
            data-testid="value-tally"
            initial={false}
            animate={{ opacity: complete ? 1 : 0, y: complete ? 0 : 8 }}
            transition={{ duration: 0.5, ease: EASE }}
            className="mt-auto flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-white/10 pt-6 text-[14px] font-medium text-[var(--strollee-bg)]/70"
          >
            {copy.tally.map((t, i) => (
              <span key={t} className="flex items-center gap-5">
                {i > 0 && <span className="h-1 w-1 rounded-full bg-white/30" />}
                {t}
              </span>
            ))}
          </motion.div>
        </div>

        {/* RIGHT — the Strollee way: video IS the panel (white studio clip), ink copy overlaid */}
        <div className="relative min-h-[560px] overflow-hidden bg-white">
          <video
            data-testid="value-video"
            poster={copy.video.posterUrl}
            aria-label={copy.video.alt}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            className="absolute left-[96px] top-4 object-contain object-center"
            style={{ height: "calc(100% - 112px)", width: "calc(100% - 96px)" }}
          >
            <source src={copy.video.url} type="video/mp4" />
            <source src={copy.video.webmUrl} type="video/webm" />
          </video>

          <div className="pointer-events-none relative flex h-full flex-col p-10 text-[var(--strollee-ink)] lg:p-14">
            <p
              data-testid="value-strollee-label"
              className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[var(--strollee-accent)]"
            >
              {copy.strolleeLabel}
            </p>
            <div className="mt-6">
              <BigNumber value={1} testid="value-strollee-count" tone="ink" />
            </div>

            <div className="mt-auto flex flex-wrap items-center gap-2" data-testid="value-mode-chips">
              {copy.stages.map((s, i) => {
                const on = i < count;
                return (
                  <motion.span
                    key={s.id}
                    data-testid={`value-mode-chip-${s.id}`}
                    initial={false}
                    animate={{
                      opacity: on ? 1 : 0.45,
                      backgroundColor: on ? "rgba(49,91,67,0.10)" : "rgba(23,23,19,0.03)",
                      borderColor: on ? "rgba(49,91,67,0.55)" : "rgba(23,23,19,0.15)",
                    }}
                    transition={{ duration: 0.4, ease: EASE }}
                    className="inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-[12px] font-semibold tracking-wide"
                  >
                    <Check
                      size={12}
                      strokeWidth={2.6}
                      className={on ? "text-[var(--strollee-accent)]" : "text-[var(--strollee-ink)]/35"}
                    />
                    {s.mode}
                  </motion.span>
                );
              })}
              <span className="ml-auto text-[15px] font-semibold tracking-[-0.01em] text-[var(--strollee-ink)]">
                {copy.strolleeHeadline}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* FOOTER ROW */}
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7, ease: EASE, delay: 0.1 }}
        className="mt-10 flex items-center justify-between gap-8 lg:mt-12"
      >
        <div data-testid="value-stat" className="flex items-baseline gap-4">
          <span className="text-[40px] font-semibold leading-none tracking-[-0.03em] lg:text-[48px]">
            {copy.statNumber}
          </span>
          <span className="max-w-[200px] text-[12px] font-medium uppercase leading-snug tracking-[0.14em] text-[var(--strollee-bg)]/45">
            {copy.statLabel}
          </span>
        </div>
        <a
          href="#modes"
          data-testid="value-cta"
          className="group inline-flex shrink-0 items-center gap-2 rounded-full border border-white/25 bg-white/5 px-6 py-3 text-sm font-semibold tracking-wide text-[var(--strollee-bg)] backdrop-blur-md transition-all duration-200 hover:-translate-y-[2px] hover:bg-white hover:text-[var(--strollee-ink)]"
        >
          {copy.cta}
          <ArrowRight
            size={16}
            strokeWidth={2.2}
            className="transition-transform duration-200 group-hover:translate-x-1"
          />
        </a>
      </motion.div>
    </section>
  );
};
